import React, { useState, useEffect, useCallback } from "react";
import {
  collection, getDocs, addDoc, setDoc, deleteDoc,
  doc, query, orderBy, getDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebase-config";

/* Converts **word** → highlighted span for the live preview */
const toHtml = (text) =>
  (text || "").replace(/\*\*(.*?)\*\*/g, '<span class="highlight">$1</span>');

/* ── SEED DATA — plain text with **highlights** ─────────────────── */
const SEED_POINTS = [
  { order: 1, text: "With 5 years of experience in **Contract Management**, I've worked across **startups**, **scaleups**, and **global teams**, drafting, reviewing, and negotiating contracts that drive clarity, manage risk, and protect business interests." },
  { order: 2, text: "From **vendor and service agreements** to **procurement contracts**, **privacy addendums**, and cross-functional legal documents, I bring a **strategic mindset** and sharp communication skills to every stage of the contract lifecycle." },
  { order: 3, text: "Beyond contract work, I've driven business growth through **legal recruiting**, optimizing contract workflows, engaging on social media, and supporting investor outreach and networking for early-stage companies." },
  { order: 4, text: "This site is a curated extension of that work, created to go beyond the bullet points of a CV. It offers insight into my approach, showcases the work behind my results, and reflects the standards I bring to every engagement: **clarity**, **consistency**, and **care**." },
  { order: 5, text: "Explore the sections below to uncover insights from my **career**, **skills**, real-world cases, practical tips, and more." },
];

const SEED_CONFIG = {
  headingPrefix: "Get to know",
  headingAccent: "me",
  linkedinUrl:   "https://www.linkedin.com/in/pooja-biswal-5ab931112/",
  linkedinLabel: "Connect with me on LinkedIn",
  introPhoto: "https://firebasestorage.googleapis.com/v0/b/pp-studio-website.firebasestorage.app/o/library%2Fhero-bg%2Fhero.jpg?alt=media&token=a4190605-7be8-4a6e-b7fe-abf5703fa6bb",
};

/* ── POINT FORM ─────────────────────────────────────────────────── */
function PointForm({ form, setForm, onSave, onCancel, saving }) {
  const f = form;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="adm-skill-form">
      <h3 className="adm-form-heading">{f.id ? "Edit Point" : "Add Point"}</h3>

      <div className="adm-intro-tip">
        <strong>Tip:</strong> Wrap any word or phrase in <strong>**double stars**</strong> to make it copper-coloured and highlighted.
        <br />Example: <code>I have **5 years** of experience in **Contract Management**</code>
      </div>

      <div className="adm-form-grid">
        <label><span>Order</span>
          <input type="number" value={f.order} onChange={e => set("order", e.target.value)} min={1} />
        </label>
        <label style={{ gridColumn:"1/-1" }}><span>Point text *</span>
          <textarea
            value={f.text}
            onChange={e => set("text", e.target.value)}
            rows={5}
            placeholder="Type your point here. Wrap **important words** in double stars to highlight them."
          />
        </label>

        {f.text && (
          <label style={{ gridColumn:"1/-1" }}>
            <span>Preview</span>
            <div
              className="adm-intro-preview"
              dangerouslySetInnerHTML={{ __html: toHtml(f.text) }}
            />
          </label>
        )}
      </div>

      <div className="adm-form-actions">
        <button className="adm-btn adm-btn-primary" onClick={() => onSave(f)} disabled={saving || !(f.text || "").trim()}>
          {saving ? "Saving…" : f.id ? "Update" : "Add Point"}
        </button>
        <button className="adm-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ── CONFIG FORM ────────────────────────────────────────────────── */
function ConfigForm({ form, setForm, onSave, onCancel, saving }) {
  const f = form;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="adm-skill-form">
      <h3 className="adm-form-heading">Heading & LinkedIn</h3>
      <div className="adm-form-grid">
        <label><span>Heading first part</span>
          <input type="text" value={f.headingPrefix || ""} onChange={e => set("headingPrefix", e.target.value)} placeholder="Get to know" />
        </label>
        <label><span>Highlighted word</span>
          <input type="text" value={f.headingAccent || ""} onChange={e => set("headingAccent", e.target.value)} placeholder="me" />
        </label>
        <label style={{ gridColumn:"1/-1" }}><span>Photo URL</span>
          <input type="text" value={f.introPhoto || ""} onChange={e => set("introPhoto", e.target.value)} placeholder="https://…" />
        </label>
        {f.introPhoto && (
          <label style={{ gridColumn:"1/-1" }}>
            <span>Preview</span>
            <img src={f.introPhoto} alt="" style={{ width: 140, borderRadius: 12, display: "block" }} />
          </label>
        )}
        <label style={{ gridColumn:"1/-1" }}><span>LinkedIn URL</span>
          <input type="text" value={f.linkedinUrl || ""} onChange={e => set("linkedinUrl", e.target.value)} />
        </label>
        <label style={{ gridColumn:"1/-1" }}><span>LinkedIn Button Text</span>
          <input type="text" value={f.linkedinLabel || ""} onChange={e => set("linkedinLabel", e.target.value)} />
        </label>
      </div>
      <div className="adm-form-actions">
        <button className="adm-btn adm-btn-primary" onClick={() => onSave(f)} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button className="adm-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ── MAIN PANEL ─────────────────────────────────────────────────── */
export default function IntroPanel() {
  const [points,     setPoints]     = useState([]);
  const [config,     setConfig]     = useState(null);
  const [tab,        setTab]        = useState("points");
  const [loading,    setLoading]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [seeding,    setSeeding]    = useState(false);
  const [pointForm,  setPointForm]  = useState(null);
  const [configForm, setConfigForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pSnap, cSnap] = await Promise.all([
        getDocs(query(collection(db,"intro_points"), orderBy("order","asc"))),
        getDoc(doc(db,"intro_config","main")),
      ]);
      setPoints(pSnap.docs.map(d => {
        const data = d.data();
        /* migrate old html-field docs to the text field transparently */
        if (!data.text && data.html) {
          data.text = data.html.replace(/<span class="highlight">(.*?)<\/span>/g, "**$1**").replace(/<[^>]+>/g, "");
        }
        return { id: d.id, ...data };
      }));
      setConfig(cSnap.exists() ? cSnap.data() : SEED_CONFIG);
    } catch(e) { console.error(e); }
    finally    { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePoint = async (form) => {
    setSaving(true);
    try {
      const { id, ...data } = form;
      const clean = { ...data, order: Number(data.order) };
      if (id) await setDoc(doc(db,"intro_points",id), clean);
      else    await addDoc(collection(db,"intro_points"), clean);
      setPointForm(null); await load();
    } finally { setSaving(false); }
  };

  const deletePoint = async (id) => {
    if (!window.confirm("Delete this point?")) return;
    await deleteDoc(doc(db,"intro_points",id)); await load();
  };

  const saveConfig = async (form) => {
    setSaving(true);
    try {
      await setDoc(doc(db,"intro_config","main"), form);
      setConfigForm(null); await load();
    } finally { setSaving(false); }
  };

  const seedAll = async () => {
    if (!window.confirm("Seed the 5 default intro points?")) return;
    setSeeding(true);
    try {
      await Promise.all([
        ...SEED_POINTS.map(p => addDoc(collection(db,"intro_points"), p)),
        setDoc(doc(db,"intro_config","main"), SEED_CONFIG),
      ]);
      await load();
    } catch(e) { alert("Seed error: " + e.message); }
    finally    { setSeeding(false); }
  };

  if (loading && !points.length) return <div className="adm-panel-loading">Loading…</div>;

  return (
    <section className="adm-panel">
      <div className="adm-panel-head">
        <div>
          <h2>✏️ Get to Know Me — Homepage Section</h2>
          <p>Edit the numbered introduction points on the homepage</p>
        </div>
        <div className="adm-panel-head-right">
          {points.length === 0 && (
            <button className="adm-btn adm-btn-seed" onClick={seedAll} disabled={seeding}>
              {seeding ? "Seeding…" : "Seed Default Points"}
            </button>
          )}
        </div>
      </div>

      <div className="adm-tab-row">
        {[["points","Intro Points"],["config","Heading & Links"]].map(([v,l]) => (
          <button key={v} className={`adm-tab-btn ${tab===v?"active":""}`} onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>

      {tab === "points" && (
        <div className="adm-tab-content">
          <div className="adm-panel-toolbar">
            <button className="adm-btn adm-btn-primary"
              onClick={() => setPointForm({ order: points.length + 1, text: "" })}>
              + Add Point
            </button>
            <span className="adm-count-badge">{points.length} points</span>
          </div>
          {pointForm && <PointForm form={pointForm} setForm={setPointForm} onSave={savePoint} onCancel={() => setPointForm(null)} saving={saving} />}
          <div className="adm-list">
            {points.length === 0 && <p className="adm-empty">No points yet — click "Seed Default Points" to start.</p>}
            {points.map(p => (
              <div key={p.id} className="adm-list-row" style={{ "--row-color": "var(--copper)" }}>
                <div className="adm-list-row-left">
                  <div className="adm-list-order">{p.order}</div>
                  <div className="adm-list-title adm-intro-snippet">
                    {(p.text || p.html || "").replace(/\*\*/g, "").replace(/<[^>]+>/g, "").slice(0, 90)}…
                  </div>
                </div>
                <div className="adm-list-actions">
                  <button className="adm-btn adm-btn-sm" onClick={() => setPointForm(p)}>Edit</button>
                  <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => deletePoint(p.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "config" && (
        <div className="adm-tab-content">
          {configForm
            ? <ConfigForm form={configForm} setForm={setConfigForm} onSave={saveConfig} onCancel={() => setConfigForm(null)} saving={saving} />
            : (
              <div className="adm-config-preview">
                <div className="adm-config-row"><strong>Heading</strong><span>"{config?.headingPrefix} <em>{config?.headingAccent}</em>"</span></div>
                {config?.introPhoto && <div className="adm-config-row"><strong>Photo</strong><img src={config.introPhoto} alt="" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8 }} /></div>}
                <div className="adm-config-row"><strong>LinkedIn Label</strong><span>{config?.linkedinLabel}</span></div>
                <div className="adm-config-row"><strong>LinkedIn URL</strong><span style={{ wordBreak:"break-all" }}>{config?.linkedinUrl}</span></div>
                <button className="adm-btn adm-btn-primary" onClick={() => setConfigForm({ ...SEED_CONFIG, ...config })}>Edit</button>
              </div>
            )
          }
        </div>
      )}
    </section>
  );
}
